/**
 * Utilities for handling file uploads and creating Blob URLs
 */

/**
 * Create a Blob URL from a File object
 * @param {File} file - The file to convert
 * @returns {string} - The Blob URL
 */
export function createBlobUrl(file) {
  return URL.createObjectURL(file);
}

/**
 * Revoke a Blob URL to free memory
 * @param {string} blobUrl - The Blob URL to revoke
 */
export function revokeBlobUrl(blobUrl) {
  if (blobUrl && typeof blobUrl === 'string') {
    URL.revokeObjectURL(blobUrl);
  }
}

/**
 * Validate file extension
 * @param {File} file - The file to validate
 * @param {string[]} allowedExtensions - Array of allowed extensions (e.g., ['.stl', '.obj'])
 * @returns {boolean} - True if file extension is allowed
 */
export function validateFileExtension(file, allowedExtensions) {
  const fileName = file.name.toLowerCase();
  return allowedExtensions.some(ext => fileName.endsWith(ext));
}

/**
 * Validate files for STL import
 * @param {File[]} files - Array of files
 * @returns {Object} - { isValid: boolean, error: string | null, stlFile: File | null }
 */
export function validateSTLFiles(files) {
  if (!files || files.length === 0) {
    return { isValid: false, error: 'No files selected' };
  }

  if (files.length !== 1) {
    return { isValid: false, error: 'STL import requires exactly 1 file' };
  }

  const file = files[0];
  if (!validateFileExtension(file, ['.stl'])) {
    return { isValid: false, error: 'File must be .stl format' };
  }

  return { isValid: true, error: null, stlFile: file };
}

/**
 * Validate files for PLY import
 * @param {File[]} files - Array of files
 * @returns {Object} - { isValid: boolean, error: string | null, plyFile: File | null }
 */
export function validatePLYFiles(files) {
  if (!files || files.length === 0) {
    return { isValid: false, error: 'No files selected' };
  }

  if (files.length !== 1) {
    return { isValid: false, error: 'PLY import requires exactly 1 file' };
  }

  const file = files[0];
  if (!validateFileExtension(file, ['.ply'])) {
    return { isValid: false, error: 'File must be .ply format' };
  }

  return { isValid: true, error: null, plyFile: file };
}

/**
 * Validate files for OBJ import
 * @param {File[]} files - Array of files
 * @returns {Object} - { isValid: boolean, error: string | null, objFile: File, mtlFile: File | null, textureFiles: File[] }
 */
export function validateOBJFiles(files) {
  if (!files || files.length === 0) {
    return { isValid: false, error: 'No files selected' };
  }

  const objFile = Array.from(files).find(f => f.name.toLowerCase().endsWith('.obj'));
  const mtlFile = Array.from(files).find(f => f.name.toLowerCase().endsWith('.mtl'));
  const textureFiles = Array.from(files).filter(f => {
    const name = f.name.toLowerCase();
    return name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png');
  });

  if (!objFile) {
    return { isValid: false, error: 'OBJ import requires a .obj file' };
  }

  // MTL is optional but warn if not found
  if (!mtlFile) {
    console.warn('No .mtl file found - importing OBJ without materials');
  }

  return {
    isValid: true,
    error: null,
    objFile,
    mtlFile: mtlFile || null,
    textureFiles
  };
}

/**
 * Convert file set to Blob URLs for loading
 * @param {Object} fileData - Object containing files { objFile, mtlFile, textureFiles }
 * @returns {Object} - Object with Blob URLs { objUrl, mtlUrl, textureUrls }
 */
export function createOBJBlobUrls(fileData) {
  const blobUrls = {
    objUrl: createBlobUrl(fileData.objFile),
    mtlUrl: fileData.mtlFile ? createBlobUrl(fileData.mtlFile) : null,
    textureUrls: fileData.textureFiles.map(f => ({
      name: f.name,
      url: createBlobUrl(f)
    }))
  };

  return blobUrls;
}

/**
 * Revoke all Blob URLs for OBJ import
 * @param {Object} blobUrls - Object with Blob URLs
 */
export function revokeOBJBlobUrls(blobUrls) {
  revokeBlobUrl(blobUrls.objUrl);
  revokeBlobUrl(blobUrls.mtlUrl);
  blobUrls.textureUrls?.forEach(t => revokeBlobUrl(t.url));
}
